
import React from 'react';
import { useRoleDashboardStats } from '@/hooks/use-role-dashboard-stats';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, CheckCircle, AlertTriangle, Scale } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const WarehouseDashboard: React.FC = () => {
  const stats = useRoleDashboardStats('warehouse');
  const navigate = useNavigate();

  if (stats.isLoading) {
    return <div className="p-6">Loading warehouse dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Warehouse Dashboard</h1>
        <p className="text-muted-foreground mt-1">Quality control and inventory management</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Inspections</CardTitle>
            <Package className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.pendingInspections || 0}</div>
            <p className="text-xs text-muted-foreground">Awaiting quality check</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed This Month</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.completedThisMonth || 0}</div>
            <p className="text-xs text-muted-foreground">Quality approved</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Quantity</CardTitle>
            <Scale className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalQuantity?.toLocaleString() || 0} kg</div>
            <p className="text-xs text-muted-foreground">All-time processed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rejected Items</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.rejectedItems || 0}</div>
            <p className="text-xs text-muted-foreground">Quality issues found</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Pending Inspections</CardTitle>
            <Button onClick={() => navigate('/warehouse')}>View All</Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.pendingProcurements?.slice(0, 5).map((procurement: any) => (
                <div key={procurement.id} className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded">
                  <div>
                    <p className="font-medium">{procurement.product_name || 'N/A'}</p>
                    <p className="text-sm text-muted-foreground">
                      {procurement.total_quantity || 0} kg • Grade: {procurement.quality_grade || 'N/A'}
                    </p>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => navigate(`/warehouse/inspect/${procurement.id}`)}
                  >
                    Inspect
                  </Button>
                </div>
              )) || <p className="text-muted-foreground">No pending inspections</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recently Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recentCompletions?.map((procurement: any) => (
                <div key={procurement.id} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{procurement.product_name || 'N/A'}</p>
                    <p className="text-sm text-muted-foreground">
                      {procurement.total_quantity || 0} kg • Grade: {procurement.quality_grade || 'N/A'}
                    </p>
                  </div>
                  <Badge variant="default">Completed</Badge>
                </div>
              )) || <p className="text-muted-foreground">No recent completions</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WarehouseDashboard;
